import { useCallback, useMemo, useRef } from "react";

import { AxiosError } from "axios";
import type { Unsubscribe } from "firebase/firestore";
import {
	Timestamp,
	collection,
	deleteDoc,
	doc,
	documentId,
	endBefore,
	getDoc,
	getDocs,
	limit,
	limitToLast,
	onSnapshot,
	orderBy,
	query,
	setDoc,
	startAfter
} from "firebase/firestore";

import { DateTime } from "luxon";

import { omit } from "ramda";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { getPaths } from "core/getPaths";

import { useFBMessagesStore } from "shared/contexts";
import { useFirebase, useToast, useUser } from "shared/hooks";
import { useMessagingApiService } from "shared/services";
import type {
	ChatMessage,
	ChatModel,
	ConversationSubjects,
	IgnoreStateUpdate,
	LogoModel,
	PaginationParamsModel,
	PostAttachmentModel
} from "shared/types";
import { conversationsEnum } from "shared/types";
import type { EntityMessagingConversationRoles } from "shared/types/FirebaseUserRole";
import type { ConversationsSettings, DBChatActivity, DBChatMessage, MessagingActivities } from "shared/types/Messages";
import { MessageStatuses } from "shared/types/Messages";
import { getDateTime } from "utils/getDateTime";
import { uuidv4 } from "utils/serviceUtils/helpers";

import useMessagingDB from "./useMessagingDB";

enum FBDocChangeType {
	ADDED = "added",
	MODIFIED = "modified",
	REMOVED = "removed"
}

const { messaging } = getPaths();

const convertDBChatMessageToMessage = (
	message: DBChatMessage
): {
	message: ChatMessage;
	conversationId: string;
} => {
	const sentAt = getDateTime(message.sentAt);
	const editedAt = message.editedAt ? getDateTime(message.sentAt) : null;
	const removedAt = message.removedAt ? getDateTime(message.removedAt) : null;

	return {
		message: {
			...omit(["conversationId", "sentAt", "removedAt", "editedAt", "status", "position"], message),
			sentAt: {
				seconds: sentAt.toUnixInteger(),
				nanoseconds: 0
			},
			editedAt: editedAt ? new Timestamp(Number(editedAt.toSeconds() / 1000), 0) : null,
			removedAt: removedAt ? new Timestamp(Number(removedAt.toSeconds() / 1000), 0) : null
		},
		conversationId: message.conversationId
	};
};

const useMessaging = () => {
	const messagesSubscriptions = useRef<
		{
			conversationId: string;
			unsub: Unsubscribe;
		}[]
	>([]);

	const activitiesSubscriptions = useRef<
		{
			conversationId: string;
			unsub: Unsubscribe;
		}[]
	>([]);

	const { setState, setInitial, ...store } = useFBMessagesStore();

	const messagingDB = useMessagingDB();

	const { t } = useTranslation();

	const { getUser, getCurrentFirestore } = useFirebase();

	const { getData: getUserData } = useUser();
	const { myProfile } = getUserData();

	const messagingApi = useMessagingApiService();

	const { showToast } = useToast();

	const navigate = useNavigate();

	const firestoreDB = useMemo(() => getCurrentFirestore(), [getCurrentFirestore]);

	const userId = useMemo(() => myProfile?.id || "", [myProfile?.id]);

	const sender = useMemo(
		() => ({
			id: myProfile?.id || "",
			name: myProfile?.name || "",
			avatar: myProfile?.avatar as LogoModel,
			shortName: myProfile?.shortName || ""
		}),
		[myProfile]
	);

	const prepareMessagePlaceholder = useCallback(
		(id: string, message: string): ChatMessage => {
			return {
				id,
				body: {
					text: message
				},
				sender,
				sentAt: new Timestamp(DateTime.now().toSeconds(), 0),
				tag: id,
				tempStatus: MessageStatuses.OUTGOING
			};
		},
		[sender]
	);

	const methods = useMemo(
		() => ({
			loadFBMessages: async ({
				conversationId,
				countingMessageSentAt,
				direction = "old"
			}: {
				conversationId: string;
				countingMessageSentAt?: number; // milliseconds
				direction?: "old" | "next";
			}) => {
				const cursor = countingMessageSentAt
					? new Timestamp(Number(countingMessageSentAt / 1000), 0)
					: new Timestamp(DateTime.now().toSeconds(), 0);

				const response = await getDocs(
					query(
						collection(doc(firestoreDB, "conversations", conversationId), "messages"),
						orderBy("sentAt", "asc"),
						orderBy(documentId(), "asc"),
						direction === "next" ? startAfter(cursor) : endBefore(cursor),
						direction === "next" ? limit(20) : limitToLast(20)
					)
				);

				const loadedMessages: ChatMessage[] = [];
				response.docs.map(doc => {
					loadedMessages.push(doc.data() as ChatMessage);
				});

				if (loadedMessages.length) {
					await messagingDB.updateMessages(loadedMessages, conversationId);
				}

				return {
					loadedMessages
				};
			},
			loadFBDraft: async (conversationId: string) => {
				const response = await getDoc(doc(firestoreDB, "conversations", conversationId, "drafts", sender.id));

				const draftMessage = response.exists() ? (response.data() as ChatMessage) : null;
				if (draftMessage) {
					await messagingDB.updateDrafts(draftMessage, conversationId);
				}

				return draftMessage as null | DBChatMessage;
			},
			subscribeToMessages: async (conversationId: string) => {
				let unsub: Unsubscribe | undefined;

				const authUser = await getUser();
				if (authUser && userId && !messagesSubscriptions.current.some(x => x.conversationId === conversationId)) {
					unsub = onSnapshot(
						query(
							collection(doc(firestoreDB, "conversations", conversationId), "messages"),
							orderBy("sentAt", "desc"),
							limit(20)
						),
						docsSnap => {
							const messages = docsSnap
								.docChanges()
								.map(change => {
									switch (change.type) {
										case FBDocChangeType.ADDED:
											return {
												...change.doc.data(),
												body: {
													...change.doc.data().body,
													text: !!change.doc.data().removedAt
														? "this message has been deleted"
														: change.doc.data().body.text
												}
											};
										case FBDocChangeType.MODIFIED:
											return change.doc.data();
									}
								})
								.filter(Boolean)
								.reverse();

							if (messages.length) {
								messagingDB.updateMessages(messages as ChatMessage[], conversationId);
							}
						}
					);

					messagesSubscriptions.current.push({
						conversationId,
						unsub
					});
				}

				return unsub;
			},
			subscribeToActivities: async (conversationId: string) => {
				let unsub: Unsubscribe | undefined;

				const authUser = await getUser();
				if (authUser && userId && !activitiesSubscriptions.current.some(x => x.conversationId === conversationId)) {
					unsub = onSnapshot(
						query(
							collection(doc(firestoreDB, "conversations", conversationId), "activities"),
							orderBy("startAt", "desc"),
							limit(20)
						),
						docsSnap => {
							const activities: DBChatActivity[] = [];
							const removedActivityIds: string[] = [];

							docsSnap.docChanges().forEach(change => {
								const activityInfo = change.doc.data() as DBChatActivity;
								switch (change.type) {
									case FBDocChangeType.REMOVED:
										removedActivityIds.push(activityInfo.id);
										break;
									default:
										activities.push(activityInfo);
								}
							});

							if (activities.length) {
								messagingDB.updateActivities(activities as DBChatActivity[]);
							}
							if (removedActivityIds.length) {
								messagingDB.deleteActivities(removedActivityIds, conversationId);
							}
						}
					);

					activitiesSubscriptions.current.push({
						conversationId,
						unsub
					});
				}

				return unsub;
			},
			getConversation: async (params: { id?: string; subject?: ConversationSubjects }) => {
				try {
					const { conversation } = await messagingApi.getConversation(params);

					await messagingDB.updateConversations([conversation]);

					return conversation;
				} catch (err) {
					console.log(err);
				}
			},
			getConversationDetails: async (params: { id: string }) => {
				try {
					const { conversation } = await messagingApi.getConversationDetails(params.id);
					return conversation;
				} catch (err) {
					console.log(err);
					return null;
				}
			},
			getConversationsList: async ({
				search,
				limit = 50,
				after,
				ignoreLoading,
				ignoreStateUpdate
			}: { search?: string } & PaginationParamsModel & IgnoreStateUpdate) => {
				try {
					!ignoreLoading && setState({ loadingConversations: true });

					if (!after && !ignoreStateUpdate) {
						setState({ conversationsListNextToken: null });
					}

					const { conversations, nextToken } = await messagingApi.getConversationsList({
						search,
						limit: limit === Number.POSITIVE_INFINITY ? 1000 : limit,
						after
					});

					await messagingDB.updateConversations(conversations, limit === Number.POSITIVE_INFINITY && !after && !search);

					setState({ conversationsListNextToken: nextToken });

					return { conversations, nextToken };
				} catch (err) {
					console.error(err);
					if (err instanceof Error && err.cause !== AxiosError.ERR_CANCELED) {
						showToast({ text: "Can't load conversations", noIcon: true });
					}
					return {
						conversationsList: [],
						conversationsListNextToken: null
					};
				} finally {
					!ignoreLoading && setState({ loadingConversations: false });
				}
			},
			updateConversation: async (params: {
				conversationId: string;
				title?: {
					text?: string;
					image?: null | string;
				};
				mute?: boolean;
				pin?: boolean;
				updateLastOpened?: boolean;
				settings?: ConversationsSettings;
				hideToast?: boolean;
			}) => {
				try {
					const { mute, pin, hideToast = false } = params;
					if (params.updateLastOpened) {
						await messagingDB.updateConversations([
							{
								id: params.conversationId,
								lastOpenedAt: DateTime.now().toISO()
							} as ChatModel
						]);
					}

					const { conversation } = await messagingApi.updateConversation(omit(["hideToast"], params));
					if (!params.updateLastOpened) {
						await messagingDB.updateConversations([conversation]);
					}
					// methods.setCurrentConversation(conversation.id);

					if (mute !== undefined && !hideToast) {
						showToast({
							text: mute ? t("messaging:conversation_muted") : t("messaging:conversation_unmuted"),
							noIcon: true
						});
					}

					if (pin !== undefined && !hideToast) {
						showToast({
							text: pin ? t("messaging:conversation_pinned") : t("messaging:conversation_unpinned"),
							noIcon: true
						});
					}
				} catch (err) {
					console.log(err);
				}
			},
			updateConversationParticipants: async (params: {
				conversationId: string;
				participants: {
					add?: {
						id: string;
						role: EntityMessagingConversationRoles;
					}[];
					remove?: {
						id: string;
						role?: EntityMessagingConversationRoles;
					}[];
				};
			}) => {
				try {
					await messagingApi.updateConversationParticipants(params);
				} catch (err) {
					console.log(err);
				}
			},
			listConversationParticipants: async (conversationId: string) => {
				try {
					const { conversation } = await messagingApi.listConversationParticipants(conversationId);
					return conversation.participants || [];
				} catch (err) {
					console.log(err);
					return [];
				}
			},
			sendMessage: async ({
				conversationId,
				message,
				attachments
			}: {
				conversationId: string;
				message: string;
				attachments?: PostAttachmentModel[];
			}) => {
				try {
					const tag = uuidv4();

					const tempMessage = prepareMessagePlaceholder(tag, message);
					await messagingDB.updateMessages([tempMessage], conversationId);

					methods.clearDraft(conversationId);

					const response = await messagingApi.sendMessage({
						conversationId,
						message: {
							tag,
							text: message
						},
						attachments
					});

					return { response };
				} catch (err) {
					console.log(err);
				}
			},
			updateActivity: async ({
				conversationId,
				activity
			}: {
				conversationId: string;
				activity: MessagingActivities;
			}) => {
				try {
					const activityInfo: DBChatActivity = {
						id: uuidv4(),
						activity,
						conversationId,
						person: sender,
						startAt: DateTime.now().toMillis()
					};
					await messagingDB.updateActivities([activityInfo]);

					await setDoc(
						// sender.id because we allow only 1 activity from 1 user in the same chat
						doc(collection(doc(firestoreDB, "conversations", conversationId), "activities"), sender.id),
						activityInfo,
						{ merge: true }
					);

					return {};
				} catch (err) {
					console.log(err);
				}
			},
			removeActivity: async (conversationId: string) => {
				try {
					await messagingDB.deleteActivity({ conversationId, personId: sender.id });
					await deleteDoc(doc(collection(doc(firestoreDB, "conversations", conversationId), "activities"), sender.id));

					return {};
				} catch (err) {
					console.log(err);
				}
			},
			updateMessage: async ({ conversationId, message }: { conversationId: string; message: DBChatMessage }) => {
				try {
					await messagingDB.updateMessages([convertDBChatMessageToMessage(message).message], conversationId);
					await messagingApi.updateMessage(conversationId, message.id, message.body.text);
				} catch (err) {
					console.log(err);
				}
			},
			deleteMessage: async (conversationId: string, messageId: string) => {
				setState({
					deletingMessage: true
				});

				try {
					const response = await messagingApi.deleteMessage(conversationId, messageId);
					await messagingDB.deleteMessage(messageId);

					showToast({
						text: "Message successfully deleted",
						noIcon: true
					});

					setState({ deleteMessageDialog: undefined });

					return response;
				} catch (err) {
					console.log(err);
				} finally {
					setState({
						deletingMessage: false
					});
				}
			},
			setCurrentConversation: async (id: string) => {
				const conversationsList = await messagingDB.getConversations();
				const conversation = conversationsList.find(x => x.id === id);
				if (conversation) {
					methods.subscribeToMessages(conversation.id);
					methods.subscribeToActivities(conversation.id);

					const chatImage =
						conversation.lastParticipants?.length === 2 &&
						conversation.subject?.type === conversationsEnum.CORE_USER_GENERAL
							? conversation.lastParticipants.find(x => x.id !== userId)?.avatar || null
							: conversation.title?.image;

					const chatTitle =
						conversation.lastParticipants?.length === 2 &&
						conversation.subject?.type === conversationsEnum.CORE_USER_GENERAL
							? (conversation.lastParticipants.find(x => x.id !== userId)?.name ?? "")
							: (conversation.title?.text ?? "");

					setState({
						currentConversation: {
							...conversation,
							title: {
								...conversation.title,
								image: chatImage,
								text: chatTitle
							},
							subject: conversation.subject!
						}
					});
				}
			},
			setDeleteMessageDialog: (deleteMessageDialog?: { open: boolean; message?: DBChatMessage }) => {
				setState({ deleteMessageDialog });
			},
			setEditMessageDialog: (editMessage?: DBChatMessage) => {
				setState({ editMessage });
			},
			handleDraft: async (conversationId: string, message: string) => {
				try {
					const tag = uuidv4();
					const draftMessage = prepareMessagePlaceholder(tag, message);
					await messagingDB.updateDrafts(draftMessage, conversationId);

					await setDoc(
						// sender.id because we allow only 1 draft from 1 user in the same chat
						doc(collection(doc(firestoreDB, "conversations", conversationId), "drafts"), sender.id),
						draftMessage,
						{ merge: true }
					);

					return {};
				} catch (err) {
					console.log(err);
				}
			},
			clearDraft: async (conversationId: string) => {
				try {
					await messagingDB.deleteDraft({ conversationId, personId: sender.id });
					await deleteDoc(doc(collection(doc(firestoreDB, "conversations", conversationId), "drafts"), sender.id));

					return {};
				} catch (err) {
					console.log(err);
				}
			},
			resetStore() {
				setInitial();
			}
		}),
		[
			firestoreDB,
			messagingDB,
			sender,
			getUser,
			userId,
			messagingApi,
			setState,
			showToast,
			t,
			prepareMessagePlaceholder,
			setInitial
		]
	);

	const goToChat = useCallback(
		async (conversationType: conversationsEnum, params: string[]) => {
			const conversation = await methods.getConversation({
				subject: {
					type: conversationType,
					params
				}
			});
			if (conversation) {
				navigate(`${messaging.getPath()}/${conversation.id}`);
			}
		},
		[methods, navigate]
	);

	const unsubscribeFromMessages = useCallback(() => {
		(messagesSubscriptions.current || []).forEach(({ unsub }) => {
			unsub();
		});
		messagesSubscriptions.current = [];
	}, []);

	const unsubscribeFromActivities = useCallback(
		(conversationId?: string) => {
			(activitiesSubscriptions.current || [])
				.filter(x => (conversationId ? x.conversationId === conversationId : true))
				.forEach(({ conversationId, unsub }) => {
					unsub();
					messagingDB.deleteActivity({ conversationId, personId: sender.id });
					deleteDoc(doc(collection(doc(firestoreDB, "conversations", conversationId), "activities"), sender.id));
				});
			activitiesSubscriptions.current = conversationId
				? activitiesSubscriptions.current.filter(x => x.conversationId !== conversationId)
				: [];
		},
		[firestoreDB, messagingDB, sender.id]
	);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, goToChat, unsubscribeFromMessages, unsubscribeFromActivities, getData };
};

export default useMessaging;
